import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useIntl } from 'react-intl';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { WbsLabelValueSearchResult, LabelType } from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import {
  MORE_OPTIONS_ID,
  useMoreMessage
} from 'modules/common/hooks/useMoreMessage';
import { useLabelName } from 'modules/common/hooks/useLabelName';

import FacetDetailField from './FacetDetailsField';
import SearchAutocomplete from './SearchAutocomplete';
import OptionItem from './OptionItem';

const useShowMoreClasses = makeStyles((theme) => ({
  root: {
    fontStyle: 'italic',
    fontSize: theme.typography.caption.fontSize,
    color: '#666666'
  }
}));

export type SearchHandlerParams = {
  id: string;
  query?: string;
};

export type LabelFacetDetailsProps = {
  selected: WbsLabelValueSearchResult[];
  setSelected: Dispatch<SetStateAction<WbsLabelValueSearchResult[]>>;
  label: TimesheetLabel;
  icon?: JSX.Element;
  options: WbsLabelValueSearchResult[];
  loading: boolean;
  hasMore?: boolean;
  searchHandler: (params: SearchHandlerParams) => void;
  noOptionsText?: React.ReactNode;
  onDropdownValueChange?: (mappedValue: any, label: any) => void;
  isDisable?: (label: any) => boolean;
  settingDopDownValue?: (label: any, setSelected: any) => void;
};

type CustomLabelValue = {
  id: string;
  name: string;
};

export type LabelValueType = WbsLabelValueSearchResult & {
  key: string;
  value: string;
  label: string;
  labelId: string;
  values?: CustomLabelValue[];
};

export const mappedLabelValues = (
  values: WbsLabelValueSearchResult[],
  labelId: string
): LabelValueType[] =>
  values.map((value) => ({
    ...value,
    value: value.id!,
    label: value.displayText!,
    key: value.displayText!,
    labelId: labelId
  }));

export const LabelFacetDetails: React.FC<LabelFacetDetailsProps> = ({
  selected,
  setSelected,
  label,
  options,
  loading,
  searchHandler,
  icon,
  noOptionsText,
  hasMore = false,
  onDropdownValueChange,
  isDisable,
  settingDopDownValue
}: LabelFacetDetailsProps) => {
  const { formatMessage } = useIntl();
  const moreItemsClasses = useShowMoreClasses();

  useEffect(() => {
    if (settingDopDownValue) {
      settingDopDownValue(label, setSelected);
    }
  }, [label, setSelected, settingDopDownValue]);

  const [searchTerm, setSearchTerm] = useState('');
  const { message } = useMoreMessage(label, 'displayText');

  const labelName = useLabelName(label);

  const onInputChange = useCallback(
    (_, val) => {
      setSearchTerm(val);
      searchHandler({ id: label.id, query: val });
    },
    [setSearchTerm, searchHandler, label.id]
  );

  const getOptionDisabled = useCallback(
    (option: WbsLabelValueSearchResult) => {
      return (
        option?.id === MORE_OPTIONS_ID ||
        selected.some((v) => v.id === option.id)
      );
    },
    [selected]
  );

  const getOptionSelected = useCallback(
    (option: WbsLabelValueSearchResult, value: WbsLabelValueSearchResult) => {
      return option.displayText === value.displayText;
    },
    []
  );

  const getOptionLabel = useCallback(
    (option: WbsLabelValueSearchResult) => (option && option.displayText) ?? '',
    []
  );

  const renderOption = useCallback(
    (props: any, option: WbsLabelValueSearchResult) => {
      if (option?.id === MORE_OPTIONS_ID && !loading) {
        return (
          <li {...props}>
            <Typography classes={moreItemsClasses}>
              {option.displayText}
            </Typography>
          </li>
        );
      }

      return (
        <li {...props}>
          <OptionItem value={option.displayText} icon={icon} />
        </li>
      );
    },
    [icon, moreItemsClasses, loading]
  );

  const onValueChange = useCallback(
    (selectedOptions) => {
      const mappedValue = mappedLabelValues(selectedOptions, label.id);
      setSelected(mappedValue);
      if (onDropdownValueChange) {
        onDropdownValueChange(mappedValue, label);
      }
    },
    [label, onDropdownValueChange, setSelected]
  );
  const onClick = useCallback(() => {
    searchHandler({ id: label.id, query: searchTerm });
  }, [searchHandler, searchTerm, label.id]);

  const noOptionsMessage = useMemo(
    () =>
      noOptionsText
        ? noOptionsText
        : formatMessage({ id: 'search.facets.noneOption' }),
    [noOptionsText, formatMessage]
  );

  const filterOptions = useCallback(
    (options, state) => {
      const defaultFilterOptions = createFilterOptions();
      const filteredOptions = defaultFilterOptions(options, state);
      hasMore && !loading && filteredOptions.push(message);
      return filteredOptions as WbsLabelValueSearchResult[];
    },
    [hasMore, message, loading]
  );

  const disable = useMemo(() => {
    if (isDisable) {
      return isDisable(label);
    }
    return false;
  }, [isDisable, label]);
  return (
    <FacetDetailField label={labelName}>
      <SearchAutocomplete
        dataTestId={`search-auto-complete-${
          label.type === LabelType.Select
            ? label.name.toLowerCase()
            : label.type.toLowerCase()
        }`}
        loading={loading}
        hasMore={hasMore}
        options={options}
        value={selected}
        onChange={onValueChange}
        noOptionsText={noOptionsMessage}
        getOptionDisabled={getOptionDisabled}
        getOptionSelected={getOptionSelected}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        variant="standard"
        hiddenLabel
        onInputChange={onInputChange}
        onClick={onClick}
        filterOptions={filterOptions}
        disabled={disable}
        label={label.name}
      />
    </FacetDetailField>
  );
};

export default LabelFacetDetails;
